'use client';

import React from 'react';
import styled from 'styled-components';

export interface AppShellProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export interface TopBarProps extends React.HTMLAttributes<HTMLElement> {
  /** Bar height in pixels. Defaults to 52. */
  height?: number;
  children?: React.ReactNode;
}

export interface SideNavProps extends React.HTMLAttributes<HTMLElement> {
  /** Expanded width in pixels. Defaults to 216. */
  width?: number;
  /** Collapsed width in pixels. Defaults to 56. */
  collapsedWidth?: number;
  /** When true, renders at `collapsedWidth`. */
  collapsed?: boolean;
  children?: React.ReactNode;
}

export interface ContentProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
}

export interface RightRailProps extends React.HTMLAttributes<HTMLElement> {
  /** Rail width in pixels. Defaults to 320. */
  width?: number;
  children?: React.ReactNode;
}

const Root = styled.div`
  display: grid;
  grid-template-areas:
    'top top top'
    'nav content rail';
  grid-template-rows: auto 1fr;
  grid-template-columns: auto 1fr auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${(p) => p.theme.colors.neutral.bg};
  color: ${(p) => p.theme.colors.neutral.ink};
  font-family: ${(p) => p.theme.fontFamily.ui};
`;

const Banner = styled.header<{ $h: number }>`
  grid-area: top;
  height: ${(p) => p.$h}px;
  display: flex;
  align-items: center;
  background: ${(p) => p.theme.colors.neutral.panel};
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral.line};
`;

const Nav = styled.nav<{ $w: number }>`
  grid-area: nav;
  width: ${(p) => p.$w}px;
  background: ${(p) => p.theme.colors.neutral.panel};
  border-right: 1px solid ${(p) => p.theme.colors.neutral.line};
  overflow: hidden;
  transition: width ${(p) => p.theme.transition.layout};
`;

const Main = styled.main`
  grid-area: content;
  min-width: 0;
  min-height: 0;
  overflow: auto;
`;

const Rail = styled.aside<{ $w: number }>`
  grid-area: rail;
  width: ${(p) => p.$w}px;
  background: ${(p) => p.theme.colors.neutral.panel};
  border-left: 1px solid ${(p) => p.theme.colors.neutral.line};
  overflow: auto;
`;

/**
 * Application shell composite. Compound children (`AppShell.TopBar`,
 * `AppShell.SideNav`, `AppShell.Content`, `AppShell.RightRail`) lay out
 * via CSS grid into the canonical four-region layout used across ops
 * tools. Any region except Content is optional. Generic and unstyled
 * beyond neutral surfaces and hairline dividers — consumers fill the
 * regions with their own primitives.
 */
export function AppShell({ children, ...rest }: AppShellProps) {
  return <Root {...rest}>{children}</Root>;
}

function TopBar({ height = 52, children, ...rest }: TopBarProps) {
  return (
    <Banner $h={height} {...rest}>
      {children}
    </Banner>
  );
}

function SideNav({
  width = 216,
  collapsedWidth = 56,
  collapsed = false,
  children,
  style,
  ...rest
}: SideNavProps) {
  const w = collapsed ? collapsedWidth : width;
  return (
    <Nav $w={w} style={{ width: `${w}px`, ...style }} {...rest}>
      {children}
    </Nav>
  );
}

function Content({ children, ...rest }: ContentProps) {
  return <Main {...rest}>{children}</Main>;
}

function RightRail({ width = 320, children, style, ...rest }: RightRailProps) {
  return (
    <Rail $w={width} style={{ width: `${width}px`, ...style }} {...rest}>
      {children}
    </Rail>
  );
}

AppShell.TopBar = TopBar;
AppShell.SideNav = SideNav;
AppShell.Content = Content;
AppShell.RightRail = RightRail;
